export function moveBudgetFor(config, input) {
    const cfg = config.moveBudget;
    return Math.max(formulaBudget(config, input), solutionFloor(cfg, input.solutionLength));
}
/**
 * Module 83 (WP-83.2) — the budget is never below a solution the game has in
 * hand: `max(length + extra, ceil(length × multiplier))`, shipped with both
 * margins at zero, so the floor is exactly the stored route's length. The route
 * is straight full slides; a player's drag can stop part-way and turn corners in
 * one move, so the route already overstates par (Daily 2026-10-02: route 16,
 * drag par 10, the user cleared it in 12) and stacking a margin on it only
 * inflates budgets that were fine — which is what the first cut did (15 → 18).
 *
 * Why this exists: the formula below is `proxyPar × multiplier`, and `proxyPar`
 * is a depth-limited LOWER bound that read 7 on boards whose true par was
 * 16–20. Measured over puzzles 1–40 of each track before this floor: 24 boards
 * had a budget below the shortest solution any search could find, three of
 * them proven impossible by exhaustive search (Intermediate 12: 15 moves for a
 * 16-move puzzle; Expert 7: 20 for 22; Expert 34: 18 for 20). The old DFS
 * witness (`clearingDepth`, hundreds of moves) was always capped away and never
 * caught one. A player who plays perfectly must not hit the wall.
 */
function solutionFloor(cfg, length) {
    if (length === null || length === undefined)
        return 0;
    return Math.max(length + cfg.solution.extra, Math.ceil(length * cfg.solution.multiplier));
}
function formulaBudget(config, input) {
    const cfg = config.moveBudget;
    // A board we could not verify must never be the one that walls a player, so it
    // is not given a par-derived budget at all — neither term of the formula
    // exists for it.
    if (input.usedFallback || input.proxyPar === null) {
        return Math.max(cfg.fallback.floor, Math.ceil(input.blockCount * cfg.fallback.blocksMultiplier));
    }
    // Module 65 (WP-65.A3) — the track wins when the caller knows it. A caller
    // that does not (the Studio, a fixture, a preview) keeps the band multiplier,
    // so nothing outside a real track's click path changes behaviour.
    // Module 146 — the Daily has its own multiplier, ahead of the track it borrows.
    const base = (input.daily ? cfg.dailyMultiplier : undefined) ??
        (input.track ? cfg.multiplierPerTrack[input.track] : undefined) ??
        cfg.multiplierPerBand[input.difficulty] ??
        1;
    const multiplier = withEntryGrace(config, base, input);
    // `perBlock >= 1` is a schema constraint, so `predicted >= blockCount` and the
    // "every block must leave, one per move" lower bound on par still holds.
    const predicted = input.blockCount * cfg.parEstimate.perBlock +
        input.dependencies * cfg.parEstimate.perDependency;
    const estimate = Math.ceil(Math.max(input.proxyPar, predicted) * multiplier);
    // The witness still raises a budget that came out under it — that is the only
    // provable half — but it may no longer replace it. Uncapped it erased the wall
    // on two thirds of levels; see the module header.
    return Math.min(Math.max(estimate, input.clearingDepth), Math.ceil(estimate * cfg.witnessCap));
}
/**
 * Module 69 — the opening-puzzle grace, folded into the multiplier.
 *
 * Linear from the track's peak (`entryGrace.multiplierPerTrack[track]`, else
 * `entryGrace.multiplier`; Module 146) at ordinal 1 down to `base` at ordinal
 * `puzzles + 1`, and **never below `base`**: the whole argument for the field is
 * that a track's first boards are harder than the number on them says, so a
 * configuration where it tightened one would be a bug, not a tuning choice. That
 * `max` is also what makes a peak set below a track's own multiplier a no-op.
 *
 * Needs BOTH a track and an ordinal. The Studio, a fixture, a preview and the
 * daily challenge all call without one, and every one of them keeps the plain
 * track/band multiplier — the daily deliberately, since it is one board a day
 * rather than the opening of anything.
 *
 * Module 86 — and only for a player who is meeting the track cold
 * (`entryGraceEligible`, see the input).
 */
function withEntryGrace(config, base, input) {
    const grace = config.moveBudget.entryGrace;
    if (!input.track || input.ordinal === undefined)
        return base;
    if (input.entryGraceEligible === false)
        return base;
    if (grace.puzzles <= 0 || input.ordinal < 1 || input.ordinal > grace.puzzles)
        return base;
    // Module 146 — the peak is per track; a track the config leaves out uses the shared one.
    const peak = grace.multiplierPerTrack[input.track] ?? grace.multiplier;
    const decayed = base + (peak - base) * (1 - (input.ordinal - 1) / grace.puzzles);
    return Math.max(base, decayed);
}
/** Remaining moves, or `null` when this board carries no budget (see the type). */
export function movesRemaining(state) {
    return state.moveBudget === undefined ? null : Math.max(0, state.moveBudget - state.moveCount);
}
/** True when the player has spent the whole budget on this attempt. */
export function isOutOfMoves(state) {
    const left = movesRemaining(state);
    return left !== null && left <= 0;
}
